import React from 'react';
import { Trans, t } from '@lingui/macro';
import { Flex, Text, CrownFilled, Box } from '@landr/maestro';
import { useLocation } from 'react-router-dom';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';
import FreemiumGatingCta from 'components/FreemiumGatingCta/FreemiumGatingCta';
import { RoutePaths } from 'types/enums';
import { useFreemiumGatingContext } from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { useEligibleForFreeTrial } from '@landr/pricing';
import { getDetailsViewSizes } from '../helpers/getDetailsViewSizes';

export const useUpsellBanner = () => {
    const { isAppGated, isLoading } = useFreemiumGatingContext();
    const currentBreakpoint = useGetCurrentBreakpoint();
    const { isMd } = getDetailsViewSizes({
        currentBreakpoint,
    });

    const upsellCopy = t`Access the entire catalog and load up on sample credits to fuel your creativity`;

    // do not show if showExperimentVariant and not download, favorites or user collection
    const { pathname } = useLocation();

    const userBaseRoutePaths = [
        RoutePaths.UserCollections,
        RoutePaths.Downloads,
        RoutePaths.Favorites,
    ];

    const isUserBasePage = userBaseRoutePaths.some(
        (route) => pathname.indexOf(route) !== -1,
    );

    const showUpsellBanner = !isLoading && isAppGated && isUserBasePage;

    return {
        showUpsellBanner,
        upsellCopy,
        isMd,
    };
};

export const UpsellBanner = () => {
    const { showUpsellBanner, upsellCopy, isMd } = useUpsellBanner();

    const { isEligibleForTrial } = useEligibleForFreeTrial();

    const flexDirection = isMd ? 'column' : 'row';
    const alignItems = isMd ? 'start' : 'center';

    if (!showUpsellBanner) {
        return null;
    }

    return (
        <Flex
            flexDirection={flexDirection}
            padding="lg"
            alignItems={alignItems}
            justifyContent="space-between"
            borderRadius="base"
            backgroundColor="primary.300/8"
            marginTop="md"
        >
            <Flex
                marginRight={isMd ? 0 : 'xxl'}
                marginBottom={isMd ? 'xxs' : 0}
                alignItems="center"
            >
                <Flex
                    mr="xs"
                    height="30px"
                    verticalAlign="middle"
                    alignItems="center"
                >
                    <CrownFilled size="sm" color="warning.500" />
                </Flex>
                <Text>{upsellCopy}</Text>
            </Flex>
            <Box marginLeft="28px">
                {isEligibleForTrial ? (
                    <FreemiumGatingCta
                        analyticVariant="UpsellBanner"
                        kind="link"
                        variant="warning"
                        isEligibleForTrial={isEligibleForTrial}
                    >
                        <Trans>Start free trial</Trans>
                    </FreemiumGatingCta>
                ) : (
                    <FreemiumGatingCta
                        analyticVariant="PartnerUpsell"
                        kind="link"
                        variant="warning"
                        isEligibleForTrial={isEligibleForTrial}
                    >
                        <Trans>Unlock with a plan</Trans>
                    </FreemiumGatingCta>
                )}
            </Box>
        </Flex>
    );
};
